const FALLBACK_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

async function fetchIceServers() {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${backendUrl}/api/ice-servers`);
    if (!res.ok) throw new Error('ice-servers fetch failed');
    const data = await res.json();
    return data.iceServers || FALLBACK_ICE_SERVERS;
  } catch {
    return FALLBACK_ICE_SERVERS;
  }
}

class WebRTCService {
  constructor() {
    this.ws = null;
    this.localStream = null;
    this.peers = new Map();
    this.remoteStreams = new Map();
    this.roomId = null;
    this.userId = null;
    this.userInfo = null;
    this.iceServers = FALLBACK_ICE_SERVERS;
    this.onRemoteStream = null;
    this.onRemoteStreamRemoved = null;
    this.onUserJoined = null;
    this.onUserLeft = null;
    this.onChatMessage = null;
    this.onReaction = null;
    this.onGameAction = null;
    this.onRoomState = null;
    this.onConnectionStateChange = null;
    this.onError = null;
  }

  getWebSocketUrl(roomId, userId, userInfo) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    let wsProtocol, host;
    if (backendUrl) {
      wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
      host = backendUrl.replace(/^https?:\/\//, '');
    } else {
      wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      host = window.location.host;
    }
    const params = new URLSearchParams({
      display_name: userInfo.displayName || 'Anonymous',
      avatar: userInfo.avatar || '',
      room_title: userInfo.roomTitle || '',
      tags: (userInfo.tags || []).join(',')
    });
    return `${wsProtocol}://${host}/api/ws/${roomId}/${userId}?${params.toString()}`;
  }

  async connect(roomId, userId, userInfo, localStream) {
    this.disconnect();
    this.iceServers = await fetchIceServers();

    return new Promise((resolve, reject) => {
      this.roomId = roomId;
      this.userId = userId;
      this.userInfo = userInfo;
      this.localStream = localStream;
      const wsUrl = this.getWebSocketUrl(roomId, userId, userInfo);
      console.log('[WebRTC] Connecting to signaling server:', wsUrl);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[WebRTC] WebSocket connected');
        this.onConnectionStateChange?.('connected');
        resolve();
      };

      this.ws.onerror = (error) => {
        console.error('[WebRTC] WebSocket error:', error);
        this.onError?.('Connection error');
        reject(error);
      };

      this.ws.onclose = (event) => {
        console.log('[WebRTC] WebSocket closed:', event.code, event.reason);
        this.onConnectionStateChange?.('disconnected');
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleSignalingMessage(message);
        } catch (e) {
          console.error('[WebRTC] Failed to parse message:', e);
        }
      };
    });
  }

  async handleSignalingMessage(message) {
    console.log('[WebRTC] Received message type:', message.type);

    switch (message.type) {
      case 'room_state':
        console.log('[WebRTC] Room state - users:', message.users?.map(u => u.id));
        this.onRoomState?.(message);
        // Create offers to all existing users in the room
        if (message.users) {
          for (const user of message.users) {
            if (user.id !== this.userId) {
              console.log('[WebRTC] Creating offer to existing user:', user.id);
              await this.createPeerConnection(user.id, true);
            }
          }
        }
        break;

      case 'user_joined':
        console.log('[WebRTC] User joined:', message.user_id);
        this.onUserJoined?.(message.user_info || { id: message.user_id });
        // The new user will send us an offer via room_state handling
        break;

      case 'user_left':
        console.log('[WebRTC] User left:', message.user_id);
        this.removePeer(message.user_id);
        this.onUserLeft?.(message.user_id);
        break;

      case 'signal':
        console.log('[WebRTC] Signal from:', message.from_user, 'type:', message.signal?.type);
        await this.handleSignal(message.from_user, message.signal);
        break;

      case 'chat':
        this.onChatMessage?.(message);
        break;

      case 'reaction':
        this.onReaction?.(message);
        break;

      case 'game_action':
        this.onGameAction?.(message);
        break;

      default:
        console.log('[WebRTC] Unknown message type:', message.type);
        break;
    }
  }

  /**
   * Handle WebRTC signaling (offer/answer/ice)
   * KEY FIX: sdp is now sent as a plain string, not nested object
   */
  async handleSignal(fromUser, signal) {
    if (!signal || !signal.type) {
      console.warn('[WebRTC] Invalid signal received:', signal);
      return;
    }

    try {
      if (signal.type === 'offer') {
        console.log('[WebRTC] Processing offer from:', fromUser);
        const pc = await this.createPeerConnection(fromUser, false);

        // FIX: Construct proper RTCSessionDescriptionInit with sdp as string
        await pc.setRemoteDescription({
          type: 'offer',
          sdp: signal.sdp
        });

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        // FIX: Send sdp as string, not as object
        this.sendSignal(fromUser, {
          type: 'answer',
          sdp: answer.sdp
        });
        console.log('[WebRTC] Sent answer to:', fromUser);

      } else if (signal.type === 'answer') {
        console.log('[WebRTC] Processing answer from:', fromUser);
        const pc = this.peers.get(fromUser);
        if (pc) {
          await pc.setRemoteDescription({
            type: 'answer',
            sdp: signal.sdp
          });
          console.log('[WebRTC] Remote description set for:', fromUser);
        } else {
          console.warn('[WebRTC] No peer connection found for answer from:', fromUser);
        }

      } else if (signal.type === 'ice_candidate') {
        const pc = this.peers.get(fromUser);
        if (pc && signal.candidate) {
          try {
            await pc.addIceCandidate(signal.candidate);
            console.log('[WebRTC] Added ICE candidate from:', fromUser);
          } catch (e) {
            console.error('[WebRTC] Error adding ICE candidate:', e);
          }
        }
      }
    } catch (e) {
      console.error('[WebRTC] Error handling signal:', signal.type, 'from:', fromUser, e);
    }
  }

  /**
   * Create a peer connection for a remote user
   */
  async createPeerConnection(remoteUserId, createOffer) {
    // Return existing connection if already set up
    if (this.peers.has(remoteUserId)) {
      console.log('[WebRTC] Reusing existing peer connection for:', remoteUserId);
      return this.peers.get(remoteUserId);
    }

    console.log('[WebRTC] Creating new peer connection for:', remoteUserId, 'createOffer:', createOffer);

    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    this.peers.set(remoteUserId, pc);

    // Add local stream tracks to the connection
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        console.log('[WebRTC] Adding local track:', track.kind, 'to peer:', remoteUserId);
        pc.addTrack(track, this.localStream);
      });
    } else {
      console.warn('[WebRTC] No local stream available when creating peer connection');
    }

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendSignal(remoteUserId, {
          type: 'ice_candidate',
          candidate: {
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex
          }
        });
      }
    };

    // Handle ICE connection state
    pc.oniceconnectionstatechange = () => {
      console.log('[WebRTC] ICE connection state for', remoteUserId, ':', pc.iceConnectionState);
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log('[WebRTC] Connection state for', remoteUserId, ':', pc.connectionState);
      if (pc.connectionState === 'connected') {
        console.log('[WebRTC] Successfully connected to peer:', remoteUserId);
      }
      if (pc.connectionState === 'failed') {
        console.warn('[WebRTC] Connection failed for:', remoteUserId, '- attempting cleanup');
        this.removePeer(remoteUserId);
      }
    };

    // Handle remote tracks (THIS is where we receive the other person's video/audio)
    pc.ontrack = (event) => {
      console.log('[WebRTC] Received remote track:', event.track.kind, 'from:', remoteUserId);
      const [remoteStream] = event.streams;
      if (remoteStream) {
        console.log('[WebRTC] Got remote stream from:', remoteUserId, 'tracks:', remoteStream.getTracks().length);
        this.remoteStreams.set(remoteUserId, remoteStream);
        this.onRemoteStream?.(remoteUserId, remoteStream);
      }
    };

    // Create and send offer if we are the initiator
    if (createOffer) {
      try {
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true
        });
        await pc.setLocalDescription(offer);

        // FIX: Send sdp as a plain string
        this.sendSignal(remoteUserId, {
          type: 'offer',
          sdp: offer.sdp
        });
        console.log('[WebRTC] Sent offer to:', remoteUserId);
      } catch (e) {
        console.error('[WebRTC] Error creating offer:', e);
      }
    }

    return pc;
  }

  /**
   * Send signaling data to a specific user via WebSocket
   * Message format: { type, target, sdp/candidate }
   */
  sendSignal(targetUserId, signalData) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const msg = {
        type: signalData.type,
        target: targetUserId
      };
      // Only include relevant fields to keep the message clean
      if (signalData.sdp !== undefined) msg.sdp = signalData.sdp;
      if (signalData.candidate !== undefined) msg.candidate = signalData.candidate;

      console.log('[WebRTC] Sending signal:', msg.type, 'to:', targetUserId);
      this.ws.send(JSON.stringify(msg));
    } else {
      console.warn('[WebRTC] Cannot send signal - WebSocket not open');
    }
  }

  sendChat(message) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'chat', message }));
    }
  }

  sendReaction(emoji) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'reaction', emoji }));
    }
  }

  sendGameAction(action, payload) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'game_action', action, payload }));
    }
  }

  sendMuteState(type, muted) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, muted }));
    }
  }

  removePeer(userId) {
    const pc = this.peers.get(userId);
    if (pc) {
      pc.close();
      this.peers.delete(userId);
      console.log('[WebRTC] Removed peer connection for:', userId);
    }
    if (this.remoteStreams.has(userId)) {
      this.remoteStreams.delete(userId);
      this.onRemoteStreamRemoved?.(userId);
    }
  }

  async updateLocalStream(newStream) {
    this.localStream = newStream;
    for (const [userId, pc] of this.peers) {
      const senders = pc.getSenders();
      for (const track of newStream.getTracks()) {
        const sender = senders.find(s => s.track?.kind === track.kind);
        if (sender) {
          await sender.replaceTrack(track);
        } else {
          pc.addTrack(track, newStream);
        }
      }
      console.log('[WebRTC] Updated local stream for peer:', userId);
    }
  }

  getRemoteStreams() {
    return new Map(this.remoteStreams);
  }

  disconnect() {
    console.log('[WebRTC] Disconnecting...');
    this.cleanup();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  cleanup() {
    for (const [userId, pc] of this.peers) {
      pc.close();
      console.log('[WebRTC] Closed peer connection for:', userId);
    }
    this.peers.clear();
    this.remoteStreams.clear();
  }
}

const webRTCService = new WebRTCService();
export default webRTCService;
